// =============================================================================
// Module: Spectral pigment terms
// =============================================================================
/**
 * CPU half of the spectral mix in `gl/shader.frag`. Every composite mixes the
 * canvas with one constant pigment, so the pigment's Kubelka-Munk terms (K/S per
 * wavelength and luminance) are computed here once per pass instead of once per
 * pixel. The tables are the shader's own (spectral.js); keep them in sync.
 */

const SIZE = 38;
const EPSILON = 0.0001;

// Reflectance basis per wavelength: w, c, m, y, r, g, b weights.
const BASIS = [
  1.0011607271876400, 0.9705850013229620, 0.9906735573199880, 0.0210523371789306, 0.0315605737777207, 0.0095560747554212, 0.9794047525020140,
  1.0011606515972800, 0.9705924981434250, 0.9906715249619790, 0.0210564627517414, 0.0315520718330149, 0.0095581580120851, 0.9794007068431300,
  1.0011603192274700, 0.9706253487298910, 0.9906625823534210, 0.0210746178695038, 0.0315148215513658, 0.0095673245444588, 0.9793829034702610,
  1.0011586727078900, 0.9707868061190170, 0.9906181076447950, 0.0211649058448753, 0.0313318044982702, 0.0096129126297349, 0.9792943649455940,
  1.0011525984455200, 0.9713686732282480, 0.9904514808787100, 0.0215027957272504, 0.0306729857725527, 0.0097837090401843, 0.9789630146085700,
  1.0011325252899800, 0.9731632306212520, 0.9898710814002040, 0.0226738799041561, 0.0286480476989607, 0.0103786227058710, 0.9778144666940430,
  1.0010850066332700, 0.9767402231587650, 0.9882866087596400, 0.0258235649693629, 0.0246450407045709, 0.0120026452378567, 0.9747243211338360,
  1.0009968788945300, 0.9815876054913770, 0.9842906927975040, 0.0334879385639851, 0.0192960753663651, 0.0160977721473922, 0.9671984823439730,
  1.0008652515227400, 0.9862802656529490, 0.9739349056253060, 0.0519069663740307, 0.0142066612220556, 0.0267061902231680, 0.9490796575305750,
  1.0006962900094000, 0.9899491476891340, 0.9418178384601450, 0.1007490148334730, 0.0102942608878609, 0.0595555440185881, 0.9008501289409770,
  1.0005049611488800, 0.9924927015384200, 0.8173903261951560, 0.2391298997068470, 0.0076191460521811, 0.1860398265328260, 0.7631504454622400,
  1.0003080818799200, 0.9941456804052560, 0.4324728050657290, 0.5348043122727480, 0.0058980410835420, 0.5705798201161590, 0.4659221716493190,
  1.0001196660201300, 0.9951839750332120, 0.1384539782588700, 0.7978075786430300, 0.0048233247781713, 0.8614677684002920, 0.2012632804510050,
  0.9999527659684070, 0.9957567501108180, 0.0537347216940033, 0.9114498940673840, 0.0042298748350633, 0.9458790897676580, 0.0877524413419623,
  0.9998218368992970, 0.9959128182867100, 0.0292174996673231, 0.9537979630045070, 0.0040599171299341, 0.9704654864743050, 0.0457176793291679,
  0.9997386095575930, 0.9956061578345280, 0.0213136517508590, 0.9712416154654290, 0.0043533695594676, 0.9784136302844500, 0.0284706050521843,
  0.9997095516396120, 0.9945976009618540, 0.0201349530181136, 0.9793031238075880, 0.0053434425970201, 0.9795890314112240, 0.0205271767569850,
  0.9997319302106270, 0.9922157154923700, 0.0241323096280662, 0.9833801195075750, 0.0076917201010463, 0.9755335369086320, 0.0165302792310211,
  0.9997994363461950, 0.9862364527832490, 0.0372236145223627, 0.9854612465677550, 0.0135969795736536, 0.9622887553978130, 0.0145135107212858,
  0.9999003303166710, 0.9679433372645410, 0.0760506552706601, 0.9864350469766050, 0.0316975442661115, 0.9231215745131200, 0.0136003508637687,
  1.0000204065261100, 0.8912850042449430, 0.2053754719423990, 0.9867382506701410, 0.1078611963552490, 0.7934340189431110, 0.0133604258769571,
  1.0001447879365800, 0.5362024778620530, 0.5412689034604390, 0.9866178824450320, 0.4638126031687040, 0.4592701359024290, 0.0135488943145680,
  1.0002599790341200, 0.1541081190018780, 0.8158416850864860, 0.9862777767586430, 0.8470554052720110, 0.1855741036663030, 0.0139594356366992,
  1.0003557969708900, 0.0574575093228929, 0.9128177041239760, 0.9858605924440560, 0.9431854093939180, 0.0881774959955372, 0.0144434255753570,
  1.0004275378026900, 0.0315349873107007, 0.9463398301669620, 0.9854749276762100, 0.9688621506965580, 0.0543630228766700, 0.0148854440621406,
  1.0004762334488800, 0.0222633920086335, 0.9599276963319910, 0.9851769347655580, 0.9780306674736030, 0.0406288447060719, 0.0152254296999746,
  1.0005072096750800, 0.0182022841492439, 0.9662605952303120, 0.9849715740141810, 0.9820436438543060, 0.0342215204316970, 0.0154592848180209,
  1.0005251915637300, 0.0162990559732640, 0.9693259700584240, 0.9848463034157120, 0.9839236237187070, 0.0311185790956966, 0.0156018026485961,
  1.0005350960689600, 0.0153656239334613, 0.9708545367213990, 0.9847753518111990, 0.9848454841543820, 0.0295708898336134, 0.0156824871281936,
  1.0005402209748200, 0.0149111568733976, 0.9716050665281280, 0.9847380666252650, 0.9852942758145960, 0.0288108739348928, 0.0157248764360615,
  1.0005427281678400, 0.0146954339898235, 0.9719627697573920, 0.9847196483117650, 0.9855072952198250, 0.0284486271324597, 0.0157458108784121,
  1.0005438956908700, 0.0145964146717719, 0.9721272722745090, 0.9847110233919390, 0.9856050715398370, 0.0282820301724731, 0.0157556123350225,
  1.0005444821215100, 0.0145470156699655, 0.9722094177458120, 0.9847066833006760, 0.9856538499335780, 0.0281988376490237, 0.0157605443964911,
  1.0005447695999200, 0.0145228771899495, 0.9722495776784240, 0.9847045543930910, 0.9856776850338830, 0.0281581655342037, 0.0157629637515278,
  1.0005448988776200, 0.0145120341118965, 0.9722676219987420, 0.9847035963093700, 0.9856883918061220, 0.0281398910216386, 0.0157640525629106,
  1.0005449625468900, 0.0145066940939832, 0.9722765094621500, 0.9847031240775520, 0.9856936646900310, 0.0281308901665811, 0.0157645892329510,
  1.0005449892705800, 0.0145044507314479, 0.9722802433068740, 0.9847029256150900, 0.9856958798482050, 0.0281271086805816, 0.0157648147772649,
  1.0005449969930000, 0.0145038009464639, 0.9722813248265600, 0.9847028681227950, 0.9856965214637620, 0.0281260133612096, 0.0157648801149616,
];

// CIE Y (luminance) weight per wavelength.
const CMF_Y = [
  0.0000018442894440,
  0.0000062053235865,
  0.0000310096046799,
  0.0001047483849269,
  0.0003536405299538,
  0.0009514714056444,
  0.0022822631748318,
  0.0042073290434730,
  0.0066887983719014,
  0.0098883960193565,
  0.0152494514496311,
  0.0214183109449723,
  0.0334229301575068,
  0.0513100134918512,
  0.0704020839399490,
  0.0878387072603517,
  0.0942490536184085,
  0.0979566702718931,
  0.0941521856862608,
  0.0867810237486753,
  0.0788565338632013,
  0.0635267026203555,
  0.0537414167568200,
  0.0426460643574120,
  0.0316173492792708,
  0.0208852059213910,
  0.0138601101360152,
  0.0081026402038399,
  0.0046301022588030,
  0.0024913800051319,
  0.0012593033677378,
  0.0005416465221680,
  0.0002779528920067,
  0.0001471080673854,
  0.0000610327472927,
  0.0000343873229523,
  0.0000177059860053,
  0.0000072209749130,
];

const uncompand = (x) =>
  x < 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
const compand = (x) =>
  x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;

/**
 * Splits a colour into two pigments: the same lightness and chroma, with the
 * hue turned `degrees` either way in OKLab (chroma raised slightly so their
 * mix keeps the original's). Watercolour fills separate them on the paper.
 * @param {number[]} rgb - sRGB colour in [0, 1].
 * @param {number} degrees - Hue turn of each pigment.
 * @returns {number[][]} Two sRGB colours in [0, 1].
 */
export function splitPigment(rgb, degrees) {
  const [r, g, b] = rgb.slice(0, 3).map(uncompand);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const turn = (degrees * Math.PI) / 180;
  const boost = 1 / Math.max(Math.cos(turn), 0.5);
  return [-turn, turn].map((t) => {
    const c = Math.cos(t), sn = Math.sin(t);
    const a2 = (A * c - B * sn) * boost, b2 = (A * sn + B * c) * boost;
    const l2 = (L + 0.3963377774 * a2 + 0.2158037573 * b2) ** 3;
    const m2 = (L - 0.1055613458 * a2 - 0.0638541728 * b2) ** 3;
    const s2 = (L - 0.0894841775 * a2 - 1.291485548 * b2) ** 3;
    return [
      4.0767416621 * l2 - 3.3077115913 * m2 + 0.2309699292 * s2,
      -1.2684380046 * l2 + 2.6097574011 * m2 - 0.3413193965 * s2,
      -0.0041960863 * l2 - 0.7034186147 * m2 + 1.707614701 * s2,
    ].map((v) => compand(Math.min(Math.max(v, 0), 1)));
  });
}

const ks = new Float32Array(SIZE);
const terms = { ks, luminance: 0 };

/**
 * Returns the pigment's K/S per wavelength and its luminance, matching
 * `spectral_linear_to_reflectance`, `KS` and `spectral_reflectance_to_xyz(R).y`.
 * The returned object and its array are reused by the next call.
 * @param {number[]} rgb - sRGB colour in [0, 1].
 * @returns {{ks: Float32Array, luminance: number}}
 */
export function pigmentTerms(rgb) {
  let r = uncompand(rgb[0]), g = uncompand(rgb[1]), b = uncompand(rgb[2]);
  const w = Math.min(r, g, b);
  r -= w; g -= w; b -= w;
  const c = Math.min(g, b);
  const m = Math.min(r, b);
  const y = Math.min(r, g);
  const pr = Math.min(Math.max(0, r - b), Math.max(0, r - g));
  const pg = Math.min(Math.max(0, g - b), Math.max(0, g - r));
  const pb = Math.min(Math.max(0, b - g), Math.max(0, b - r));

  let luminance = 0;
  for (let i = 0, k = 0; i < SIZE; i++, k += 7) {
    const R = Math.max(EPSILON,
      w * BASIS[k] + c * BASIS[k + 1] + m * BASIS[k + 2] + y * BASIS[k + 3] +
      pr * BASIS[k + 4] + pg * BASIS[k + 5] + pb * BASIS[k + 6]);
    luminance += R * CMF_Y[i];
    ks[i] = (1 - R) * (1 - R) / (2 * R);
  }
  terms.luminance = luminance;
  return terms;
}
